import { useEffect, useState } from "react";

import { faPencil, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { RadioGroup } from "@headlessui/react";
import { useTranslation } from "react-i18next";

import usePartDefaultCategories from "../../../data/usePartDefaultCategories";
import useShipping from "../../../data/useShipping";
import useShippingProviders from "../../../data/useShippingProviders";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import { IPart } from "../../../models/Part";
import { IShippingAddress, IShippingDimensions, IShippingLabelCreateRequest, ShippingLabelType } from "../../../models/Shipping";
import { IShippingProvider } from "../../../models/ShippingProvider";
import classNames from "../../../utils/classNames";
import { ButtonInput } from "../../shared/Inputs/ButtonInput";
import LoadingWheel from "../../shared/LoadingWheel";
import PinProtect, { PinProtectState } from "../../shared/PinProtect";
import { EditAddress } from "./EditAddress";
import { EditDimensions } from "./EditDimensions";
import { FedexShipping } from "./FedexShipping";
import { ProoviaShipping } from "./ProoviaShipping";
import ShippingLabelRadio from "./ShippingLabelRadio";
import ShippingProviders from "./ShippingProviders";

interface ManageShippingModalProps {
  part: IPart;
  setOpen: (open: boolean) => void;
}

export function ManageShippingModal({
  part,
  setOpen,
}: ManageShippingModalProps) {
  const { t } = useTranslation("stock");
  const { saveHandlers } = useDefaultCRUDHandlers("Shipping Label");
  const { defaultPartCategory } = usePartDefaultCategories({ part });
  const { shippingInfo, shippingLabel, downloadShippingLabel, cancelShipping } = useShipping(part);

  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [pinState, setPinState] = useState<PinProtectState>();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const [address, setAddress] = useState<IShippingAddress>();
  const [addressLoading, setAddressLoading] = useState(true);
  const [isAddressEditing, setIsAddressEditing] = useState(false);

  const [dimensions, setDimensions] = useState<IShippingDimensions>();
  const [dimensionsLoading, setDimensionsLoading] = useState(true);
  const [isDimensionsEditing, setIsDimensionsEditing] = useState(false);

  const [shippingService, setShippingService] = useState<string>();

  const [prooviaItemId, setProoviaItemId] = useState<number>();
  const [prooviaCollection, setProoviaCollection] = useState<string>();
  const [prooviaDelivery, setProoviaDelivery] = useState<string>();

  const { shippingProviders: providers } = useShippingProviders();

  const [shippingProvider, setShippingProvider] = useState<string>();
  const [shippingProviders, setShippingProviders] = useState<IShippingProvider[]>([]);

  const [shippingLabelType, setShippingLabelType] = useState<ShippingLabelType>(ShippingLabelType.S16);

  const handleChangeProvider = (value: string) => {
    setShippingService(undefined);
    setShippingProvider(value);
  };

  const resolveAddress = () => {
    const contactAddress = shippingInfo.data?.data.contactAddress;

    return {
      addressLine1: part.addressLine1 ?? contactAddress?.addressLine1!,
      addressLine2: part.addressLine2 ?? contactAddress?.addressLine2!,
      city: part.city ?? contactAddress?.city!,
      county: part.county ?? contactAddress?.county!,
      postcode: part.postcode ?? contactAddress?.postalCode!,
      stateOrProvince: part.stateOrProvince ?? contactAddress?.stateOrProvince!,
      fullName: part.fullName ?? shippingInfo.data?.data.fullName!,
    };
  };

  const resolveDimensions = () => {
    const defaultDimensions = defaultPartCategory.data?.data;

    return {
      weight: part.weight ?? defaultDimensions?.weight!,
      height: part.height ?? defaultDimensions?.height!,
      length: part.length ?? defaultDimensions?.length!,
      width: part.width ?? defaultDimensions?.width!,
    };
  };

  const handleDeleteShipping = () => {
    setPinModalOpen(true);
    setPinState({
      onPinSuccess: () => {
        cancelShipping.mutate(null, {
          onSuccess: () => {
            saveHandlers.onSuccess();
            setOpen(false);
          },
          onError: (res: any) => {
            saveHandlers.onError();
          },
          onSettled: () => { },
        });
      },
      onPinFailed: () => { },
      pinLabel: t("Delete Tracking Information"),
    });
  };

  // Opens a label PDF in a new tab. Shared by both paths below so re-printing an existing label
  // behaves identically to printing a freshly created one.
  const openLabelBlob = (blob: Blob) => {
    const url = window.URL.createObjectURL(blob);

    if (!url) {
      setError(t("Failed to generate shipping label. Please try again."));
      return;
    }

    // Open the label in a new tab and let the user print it manually (Ctrl+P). Auto-printing via
    // a hidden iframe is unreliable because the print call is no longer tied to the user gesture
    // by the time the blob loads, so browsers silently block it.
    const tab = window.open(url, "_blank");
    if (!tab) {
      window.URL.revokeObjectURL(url);
      setError(t("Popup blocked. Please allow popups for this site to open the shipping label, then try again."));
      return;
    }

    // Revoke after a delay so the new tab has time to load the PDF.
    window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  };

  const handleGetShippingLabel = () => {

    // A label already exists: fetch the stored document rather than re-posting to /shipping, which
    // is the create endpoint. Only a guard on this same id stops that booking a second collection.
    if (part.shippingLabelDocumentId) {
      setLoading(true);
      setError(undefined);

      downloadShippingLabel.mutate(part.shippingLabelDocumentId, {
        onSuccess: (blob: any) => openLabelBlob(blob),
        onError: () => setError(t("Could not open the saved shipping label. Please try again.")),
        onSettled: () => setLoading(false),
      });

      return;
    }

    {
      if (shippingService === undefined) {
        setError(t("Please select a shipping service"));
        return;
      }

      if (!address?.addressLine1 || address?.addressLine1!.length < 4) {
        setError(
          t("Please fill in the address line 1. Must be at least 4 characters.")
        );
        return;
      }

      if (!address?.postcode || address.postcode!.length < 5) {
        setError(
          t("Please fill in the address postcode. Must be at least 5 characters.")
        );
        return;
      }

      if (
        !dimensions?.height ||
        dimensions.height === 0 ||
        !dimensions.length ||
        dimensions.length === 0 ||
        !dimensions.width ||
        dimensions.width === 0 ||
        !dimensions.weight ||
        dimensions.weight === 0
      ) {
        setError(t("Please fill in the parcel size and weight."));
        return;
      }
    }

    setLoading(true);
    setError(undefined);

    let shippingLabelCreateData: IShippingLabelCreateRequest = {
      ...address!,
      ...dimensions!,
      partDataId: part.id,
      service: shippingService!,
      labelType: shippingLabelType,
    };

    // is Proovia
    if (shippingProvider === "PROOVIA") {
      shippingLabelCreateData = {
        ...shippingLabelCreateData,
        prooviaItemId: prooviaItemId,
        collectionDate: prooviaCollection,
        deliveryDate: prooviaDelivery,
      };
    }

    shippingLabel.mutate(shippingLabelCreateData, {
      onSuccess: (d: any) => {
        saveHandlers.onSuccess();
        d?.blob().then((blob: Blob) => openLabelBlob(blob));
      },
      onError: (d: any) => {
        saveHandlers.onError();
        d.text().then((x: string) => console.warn(x));
      },
      onSettled: () => {
        setLoading(false);
      },
    });
  };

  useEffect(() => {
    if (!defaultPartCategory.isLoading && defaultPartCategory.data) {
      setDimensions(resolveDimensions());
      setDimensionsLoading(false);
    }

    if (!part.categoryId) {
      setDimensionsLoading(false);
    }
  }, [defaultPartCategory.isLoading, defaultPartCategory.data]);

  useEffect(() => {
    if (!shippingInfo.isLoading && shippingInfo.data) {
      setAddress(resolveAddress());
      setAddressLoading(false);
    }
  }, [shippingInfo.isLoading, shippingInfo.data]);

  useEffect(() => {
    if (!providers.data || providers.isLoading) return;

    const availableProviders = providers.data.data.filter((provider) => {
      const settings = provider.requiredSettings ?? {};
      const anySet = Object.values(settings).some((val) => val?.trim() !== "");
      return anySet;
    });

    setShippingProviders(availableProviders);
    if (!shippingProvider || !availableProviders.some((x) => x.providerId === shippingProvider)) {
      setShippingProvider(availableProviders[0]?.providerId);
    }

  }, [providers.data, providers.isLoading, shippingProvider]);

  return (
    <>
      <PinProtect
        open={pinModalOpen}
        setOpen={setPinModalOpen}
        pinState={pinState}
      />
      <div className="space-y-4">
        {loading ? (
          // The API holds this request open while it waits for the courier to publish the
          // label - about a minute for Proovia, and it retries for up to three. A bare wheel
          // reads as a hang, and anyone who closes the window loses sight of an order that has
          // already been placed and charged for.
          <div className="flex flex-col items-center gap-y-2">
            <LoadingWheel />
            <p className="text-sm text-gray-500 text-center max-w-sm">
              {t(
                "Booking with the courier and waiting for their label. This can take up to two minutes - please keep this window open."
              )}
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center space-x-4">
              <label className="text-lg font-bold">{t("Shipping Address")}</label>
              <FontAwesomeIcon
                className="rounded-full ml-1 cursor-pointer hover:ring-2 hover:ring-blue-500"
                icon={isAddressEditing ? faXmark : faPencil}
                onClick={() => setIsAddressEditing(!isAddressEditing)}
              />
            </div>
            <div>
              {addressLoading ? (
                <LoadingWheel />
              ) : isAddressEditing ? (
                <EditAddress
                  address={address!}
                  partId={part.id}
                  onEditSuccess={(result) => {
                    setAddress(result);
                    setIsAddressEditing(false);
                  }}
                />
              ) : (
                <>
                  <p>{address?.fullName}</p>
                  <p>{address?.addressLine1}</p>
                  <p>{address?.addressLine2}</p>
                  <p>{address?.city}</p>
                  <p>{address?.county}</p>
                  <p>{address?.stateOrProvince}</p>
                  <p>{address?.postcode}</p>
                </>
              )}
            </div>
            <div className="flex items-center space-x-4">
              <label className="text-lg font-bold">{t("Dimensions & Weight")}</label>
              <FontAwesomeIcon
                className="rounded-full ml-1 cursor-pointer hover:ring-2 hover:ring-blue-500"
                icon={isDimensionsEditing ? faXmark : faPencil}
                onClick={() => setIsDimensionsEditing(!isDimensionsEditing)}
              />
            </div>
            <div>
              {dimensionsLoading ? (
                <LoadingWheel />
              ) : isDimensionsEditing ? (
                <EditDimensions
                  dimensions={dimensions!}
                  partId={part.id}
                  categoryId={part.categoryId ?? defaultPartCategory.data?.data.id}
                  onEditSuccess={() => { }}
                />
              ) : (
                <div className="grid grid-cols-4 gap-2 text-center divide-x">
                  <p>
                    <span className="text-gray-400">{t("Width:")}</span>{" "}
                    {dimensions?.width}
                    cm
                  </p>
                  <p>
                    <span className="text-gray-400">{t("Height:")}</span>{" "}
                    {dimensions?.height}cm
                  </p>
                  <p>
                    <span className="text-gray-400">{t("Length:")}</span>{" "}
                    {dimensions?.length}cm
                  </p>
                  <p>
                    <span className="text-gray-400">{t("Weight:")}</span>{" "}
                    {dimensions?.weight}kg
                  </p>
                </div>
              )}
            </div>
            {!part.shippingLabelDocumentId && (
              <div className="flex items-center space-x-4">
                <label className="text-lg font-bold">{t("Shipping Service")}</label>
              </div>
            )}
            {addressLoading || dimensionsLoading ? (
              <div>
                <LoadingWheel />
              </div>
            ) : (
              !part.shippingLabelDocumentId && (
                <div>
                  <div className="w-full">
                    <RadioGroup
                      value={shippingProvider}
                      onChange={handleChangeProvider}
                      className="my-4"
                    >
                      <RadioGroup.Label className="sr-only">
                        {t("Choose which shipper to use")}
                      </RadioGroup.Label>
                      <div
                        className="grid gap-3"
                        style={{
                          gridTemplateColumns: `repeat(${Math.max(shippingProviders.length, 1)}, minmax(0, 1fr))`,
                        }}
                      >
                        {shippingProviders.map((type) => (
                          <RadioGroup.Option
                            key={type.providerId}
                            value={type.providerId}
                            className={({ active, checked }) =>
                              classNames(
                                active
                                  ? "ring-2 ring-offset-2 ring-primary-500"
                                  : "",
                                checked
                                  ? "bg-primary-600 border-transparent text-white hover:bg-primary-700"
                                  : "bg-white border-gray-200 text-gray-900 hover:bg-gray-50",
                                "cursor-pointer focus:outline-none border rounded-md py-1.5 px-3 flex items-center justify-center text-sm font-medium uppercase sm:flex-1"
                              )
                            }
                          >
                            <RadioGroup.Label as="span">
                              {type.providerName}
                            </RadioGroup.Label>
                          </RadioGroup.Option>
                        ))}
                      </div>
                    </RadioGroup>
                    <ShippingLabelRadio type={shippingLabelType} setType={setShippingLabelType} />
                  </div>
                  <ShippingProviders
                    part={part}
                    providerId={shippingProvider}
                    selectedProvider={shippingProviders.find((x) => x.providerId === shippingProvider)}
                    shippingService={shippingService}
                    setShippingService={setShippingService}
                    setProoviaItemId={setProoviaItemId}
                    setProoviaCollection={setProoviaCollection}
                    setProoviaDelivery={setProoviaDelivery}
                  />
                </div>
              )
            )}
            {error && (
              <div className="col-span-4 bg-red-100 text-red-600 px-4 py-2 rounded">
                {error}
              </div>
            )}
            <div className="flex items-center justify-end space-x-2">
              <ButtonInput
                label={t("Cancel Shipping")}
                disabled={part.shippingLabelDocumentId === null}
                onClick={handleDeleteShipping}
                classes=""
                isSubmit={false}
              />
              <ButtonInput
                label={t("Print Shipping Label")}
                disabled={loading || (!shippingService && !part.shippingLabelDocumentId)}
                onClick={handleGetShippingLabel}
                classes=""
                isSubmit={false}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
}
